import React, { useState, CSSProperties, useRef } from 'react';
import html2canvas from 'html2canvas';
import { IFeedbackData, IUserFeedback } from './IUserFeedback';
import { FeedbackConstants } from './Constants';
const MaxFilesUploadForUserFeedback = 5;
const MaxFileSizeForUserFeedback = 2; // 2 MB in bytes
const allowedExtensions = ['jpg', 'jpeg', 'png', 'pdf', 'doc', 'docx', 'xls', 'xlsx', 'txt'];

// Id of the single visually-hidden aria-live region used for status messages (e.g. form reset).
const FeedbackScreenReaderAnnouncerId = 'userFeedbackScreenReaderAnnouncer';

/**
 * Accessibility fix (WCAG 2.1 SC 4.1.3 Status Messages).
 * Actions such as "Reset Form" update the UI without moving focus, so NVDA/JAWS never announce the
 * resulting status change. This helper pushes text into a single visually-hidden aria-live region so
 * screen readers announce the status with no visual or focus impact. The region is cleared first so a
 * repeated/identical message is re-announced reliably.
 */
const announceToScreenReader = (message: string): void => {
    if (!message || typeof document === 'undefined') {
        return;
    }
    let liveRegion = document.getElementById(FeedbackScreenReaderAnnouncerId);
    if (!liveRegion) {
        liveRegion = document.createElement('div');
        liveRegion.id = FeedbackScreenReaderAnnouncerId;
        liveRegion.setAttribute('role', 'status');
        liveRegion.setAttribute('aria-live', 'assertive');
        liveRegion.setAttribute('aria-atomic', 'true');
        Object.assign(liveRegion.style, {
            position: 'absolute',
            width: '1px',
            height: '1px',
            padding: '0',
            margin: '-1px',
            overflow: 'hidden',
            clip: 'rect(0, 0, 0, 0)',
            whiteSpace: 'nowrap',
            border: '0',
        } as CSSProperties);
        document.body.appendChild(liveRegion);
    }
    // Clear first so an identical, consecutive message is still announced by NVDA.
    liveRegion.textContent = '';
    window.setTimeout(() => {
        if (liveRegion) {
            liveRegion.textContent = message;
        }
    }, 100);
};

export const UserFeedback: React.FC<IUserFeedback> = ({
    onClose,
    onSubmit,
    isEmbedded = false,
    caseNumber,
    claimNumber,
    applicationModule,
    opportunityId,
    claimId,
    userDetails,
    browserName
}) => {
    const [rating, setRating] = useState<number>(0);
    const [ratingComments, setRatingComments] = useState<string>('');
    const [improvement, setImprovement] = useState<string>('');
    const [issueBlocker, setIssueBlocker] = useState<string>('');
    const [performanceRating, setPerformanceRating] = useState<string>('');
    const [suggestions, setSuggestions] = useState<string>('');
    const [errorMessage, setErrorMessage] = useState<string>('');
    const [isSubmitted, setIsSubmitted] = useState<boolean>(false);
    const [attachmentList, setAttachmentList] = useState<File[]>([]);
    const [includeScreenshot, setIncludeScreenshot] = useState<boolean>(false);
    // Tracks which star radio currently has keyboard focus so a visible focus indicator can be drawn
    // on the star glyph (MAS 2.4.7); the native radios are visually hidden, so their focus ring is not visible.
    const [focusedStar, setFocusedStar] = useState<number>(0);

    // Accessibility refs
    const errorRef = useRef<HTMLDivElement>(null);
    const starsRef = useRef<HTMLDivElement>(null);
    // Which error-causing field to move focus to once the validation error has rendered
    // (MAS 2.4.3 Focus Order). Set just before setErrorMessage; consumed by the effect below.
    const pendingFocusRef = useRef<'rating' | 'comments' | 'error' | null>(null);
    // Ref to the native file input so its displayed filename can be cleared on reset (MAS 3.2.2);
    // the input is uncontrolled, so clearing React state alone leaves the old name shown/announced.
    const fileInputRef = useRef<HTMLInputElement>(null);

    // Screen-reader-only description for the file input (not visible in UI)
    const attachmentDescription = `Allowed file formats: ${allowedExtensions.join(', ')}. Maximum ${MaxFileSizeForUserFeedback} MB per file. Up to ${MaxFilesUploadForUserFeedback} files.`;

    const feedback: IFeedbackData = {
        rating,
        ratingComments,
        improvement,
        issueBlocker,
        performanceRating,
        suggestions,
        pageUrl: window.location.href,
        attachments: attachmentList,
        opportunityId,
        claimId,
        caseNumber,
        claimNumber,
        userDetails,
        applicationModule,
        browserName
    };

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files) {
            setAttachmentList([...attachmentList, ...Array.from(e.target.files)]);
            if (errorMessage) setErrorMessage('');
        }
    };

    const handleRemoveFile = (index: number): void => {
        const removedFile = attachmentList[index];
        setAttachmentList(attachmentList.filter((_, i) => i !== index));
        // Keep the uncontrolled native file input in sync so a stale filename is not shown/announced
        // and the same file can be re-selected after removal.
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
        // Accessibility (WCAG 4.1.3): announce the deletion so NVDA reports the status change.
        if (removedFile) {
            announceToScreenReader(
                FeedbackConstants.AttachmentDeletedAnnouncement.replace('{0}', removedFile.name)
            );
        }
    };

    const handleSubmit = async (): Promise<void> => {
        // Validation: Rating must be selected
        if (rating === 0) {
            // MAS 2.4.3 (Focus Order): move focus to the rating field (not the Submit button) once
            // the error renders; the role="alert" summary announces the status message.
            pendingFocusRef.current = 'rating';
            setErrorMessage(FeedbackConstants.RatingErrorMessage);
            focusErrorCausingField();
            return;
        }

        // Validation: Check if Question 1 is mandatory based on rating
        if (rating <= 3 && rating > 0 && ratingComments.trim() === '') {
            pendingFocusRef.current = 'comments';
            setErrorMessage(FeedbackConstants.RatingCommentsErrorMessage);
            focusErrorCausingField();
            return;
        }

        // Validation: Limit number of attachments
        if (attachmentList.length > MaxFilesUploadForUserFeedback) {
            pendingFocusRef.current = 'error';
            setErrorMessage(`You can upload a maximum of ${MaxFilesUploadForUserFeedback} files.`);
            focusErrorCausingField();
            return;
        }

        // Validation: Limit file size
        const oversized = attachmentList.find(
            item => item && item.size && item.size > MaxFileSizeForUserFeedback * 1024 * 1024
        );
        if (oversized) {
            pendingFocusRef.current = 'error';
            setErrorMessage(`Each file must be less than ${MaxFileSizeForUserFeedback} MB.`);
            focusErrorCausingField();
            return;
        }

        // Validation: Allow only certain file extensions
        const invalidFile = attachmentList.find(file => {
            const extMatch = file.name.match(/\.([^.]+)$/);
            const ext = extMatch ? extMatch[1].toLowerCase() : '';
            return !allowedExtensions.includes(ext);
        });
        if (invalidFile) {
            pendingFocusRef.current = 'error';
            setErrorMessage(`Only files with the following extensions are allowed: ${allowedExtensions.join(', ')}.`);
            focusErrorCausingField();
            return;
        }

        // Clear any previous error messages
        setErrorMessage('');

        let screenshotData: string = '';
        // If includeScreenshot is true, capture screenshot of the current page
        if (includeScreenshot && (!isEmbedded || applicationModule == 1)) {
            const element = document.getElementById('app-background');
            if (element) {
                const screenshotCanvas = await html2canvas(element);
                const blob = await new Promise<Blob | null>((resolve) =>
                    screenshotCanvas.toBlob((blob) => resolve(blob), FeedbackConstants.ScreenshotMimeType)
                );

                if (!blob) throw new Error(FeedbackConstants.ScreenshotError);
                const screenshotFile = new File([blob], FeedbackConstants.ScreenshotFileName, { type: FeedbackConstants.ScreenshotMimeType });
                attachmentList.push(screenshotFile);
                setAttachmentList([...attachmentList]);
            }
        }

        console.log(FeedbackConstants.FeedbackSubmitted + feedback)
        try {
            if (onSubmit) {
                onSubmit(feedback);
            }

            setIsSubmitted(true);
        } catch (error: any) {
            console.error('Error during feedback submission:', error);
            setErrorMessage('An error occurred while submitting your feedback. Please try again later.');
        }
    };

    const handleReset = (): void => {
        setRating(0);
        setRatingComments('');
        setImprovement('');
        setIssueBlocker('');
        setPerformanceRating('');
        setSuggestions('');
        setErrorMessage('');
        setIsSubmitted(false);
        setAttachmentList([]);
        // Clear the native file input's value so the previously selected file name is no longer
        // displayed or announced by NVDA (MAS 3.2.2); the input is uncontrolled and keeps its value
        // independent of React state.
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
        // Accessibility (WCAG 4.1.3): the reset updates the UI without moving focus, so announce the
        // status change through the aria-live region; otherwise NVDA stays silent after Reset Form.
        announceToScreenReader(FeedbackConstants.FormResetAnnouncement);
    };

    const handleRatingChange = (newRating: number): void => {
        setRating(newRating);
        if (errorMessage) setErrorMessage('');
    };

    const handleInputChange = (field: string, value: string): void => {
        switch (field) {
            case FeedbackConstants.RatingComments:
                setRatingComments(value);
                if (errorMessage) setErrorMessage('');
                break;
            case FeedbackConstants.Improvement:
                setImprovement(value);
                break;
            case FeedbackConstants.IssueBlocker:
                setIssueBlocker(value);
                break;
            case FeedbackConstants.PerformanceRating:
                setPerformanceRating(value);
                break;
            case FeedbackConstants.Suggestions:
                setSuggestions(value);
                break;
        }
    };

    // Move focus to the first error-causing field once the error (and any updated field state) has
    // rendered, so focus leaves the Submit button (MAS 2.4.3). The role="alert" summary still
    // announces the status message. Falls back to the error summary for non-field (attachment) errors.
    const focusErrorCausingField = (): void => {
        window.setTimeout(() => {
            const target = pendingFocusRef.current;
            let element: HTMLElement | null = null;
            if (target === 'rating') {
                element = starsRef.current?.querySelector<HTMLInputElement>('input[type="radio"]') ?? null;
            } else if (target === 'comments') {
                element = document.getElementById('ratingComments');
            }
            if (!element) {
                element = errorRef.current;
            }
            if (element) {
                element.focus();
                if (typeof element.scrollIntoView === 'function') {
                    element.scrollIntoView({ block: 'center' });
                }
            }
            pendingFocusRef.current = null;
        }, 50);
    };

    // Success state
    if (isSubmitted) {
        const successContainer = isEmbedded ? styles.embeddedContainer : styles.popupContainer;

        return (
            <div style={isEmbedded ? undefined : styles.popupOverlay}>
                <div style={successContainer}>
                    <div style={styles.success}>
                        <h3 style={styles.successTitle}>{FeedbackConstants.FeedbackThankYouMessage}</h3>
                        <p style={styles.successMessage}>
                            {FeedbackConstants.AppreciateMessage}
                        </p>
                        <div style={styles.buttonContainer}>
                            <button onClick={handleReset} style={styles.resetButton}>
                                Submit Another Feedback
                            </button>
                            {onClose && (
                                <button onClick={onClose} style={styles.closeButton}>
                                    {isEmbedded ? FeedbackConstants.BackButtonLabel : FeedbackConstants.CloseButtonLabel}
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    // Main feedback form
    const container = isEmbedded ? styles.embeddedContainer : styles.popupContainer;
    const formContainer = isEmbedded ? styles.embeddedFormContainer : styles.popupFormContainer;

    return (
        <div style={isEmbedded ? undefined : styles.popupOverlay}>
            <div style={container}>
                {!isEmbedded && onClose && (
                    <div style={styles.header}>
                        <button onClick={onClose} style={styles.closeXButton}>×</button>
                    </div>
                )}
                <div style={formContainer}>
                    <div style={styles.ratingContainer}>
                        <h5 id="rateLabel">{FeedbackConstants.RateExperienceLabel}<span style={{ color: '#c0392b' }}> *</span></h5>

                        {/* Accessible radio-based stars: native radios hidden visually but available to AT */}
                        <div
                            ref={starsRef}
                            role="radiogroup"
                            aria-labelledby="rateLabel"
                            aria-required={true}
                            aria-invalid={rating === 0}
                            style={styles.starsContainer}
                        >
                            {[1,2,3,4,5].map(star => {
                                const checked = rating === star;
                                const starLabel = star === 1 ? 'one star' : `${star} stars`;
                                return (
                                    <label key={star} title={`${star} / 5`} style={{ display: 'inline-flex', alignItems: 'center', cursor: 'pointer', lineHeight: 1 }}>
                                        <input
                                            type="radio"
                                            name="rating"
                                            value={star}
                                            checked={checked}
                                            onChange={() => handleRatingChange(star)}
                                            onFocus={() => setFocusedStar(star)}
                                            onBlur={() => setFocusedStar(0)}
                                            aria-label={starLabel}
                                            style={styles.visuallyHiddenInput}
                                        />
                                        <span
                                            aria-hidden="true"
                                            style={{
                                                ...styles.star,
                                                color: star <= rating ? '#f39c12' : '#ddd',
                                                fontSize: isEmbedded ? '30px' : '28px',
                                                outline: focusedStar === star ? '2px solid #0067b8' : 'none',
                                                outlineOffset: '2px',
                                                borderRadius: '4px',
                                            }}
                                        >
                                            ★
                                        </span>
                                    </label>
                                );
                            })}

                            <span style={styles.ratingText}>
                                {rating > 0 && `(${rating}/5 stars)`}
                            </span>
                        </div>

                        {/* Live region announces selection changes to screen readers */}
                        <div aria-live="polite" aria-atomic="true" style={styles.visuallyHidden}>
                            {rating > 0 ? `${rating} out of 5 stars selected` : ''}
                        </div>
                    </div>

                    {errorMessage && (
                        <div id="formError" ref={errorRef} role="alert" aria-atomic="true" tabIndex={-1} style={styles.error}>
                            {errorMessage}
                        </div>
                    )}

                    <div style={styles.questionContainer}>
                        <label htmlFor="ratingComments" style={styles.label}>
                            What is the primary reason for your rating? Please be as detailed as possible
                            {rating <= 3 && rating > 0 && (
                                <span aria-label="Required" style={styles.required}> *</span>
                            )}
                        </label>
                        <textarea
                            value={ratingComments}
                            id="ratingComments"
                            aria-labelledby="lable1"
                            onChange={(e) => handleInputChange(FeedbackConstants.RatingComments, e.target.value)}
                            style={{
                                ...styles.textarea,
                                border: rating <= 3 && rating > 0 ? '2px solid #c0392b' : '1px solid #ddd',
                            }}
                            rows={isEmbedded ? 3 : 2}
                            aria-required={rating <= 3 && rating > 0}
                            aria-invalid={rating <= 3 && rating > 0 && !ratingComments?.trim()}
                            aria-describedby={rating <= 3 && rating > 0 ? "ratingCommentsHelp" : undefined}
                        />
                        {rating <= 3 && rating > 0 && (
                            <div id="ratingCommentsHelp" style={styles.helpText}>
                                {FeedbackConstants.CommentsMandatoryMessage}
                            </div>
                        )}
                    </div>
                    <div style={styles.questionContainer}>
                        <label id="lable2" style={styles.label}>
                            {FeedbackConstants.ImproveExpMessage}
                        </label>
                        <textarea
                            value={improvement}
                            aria-labelledby="lable2"
                            onChange={(e) => handleInputChange(FeedbackConstants.Improvement, e.target.value)}
                            style={styles.textarea}
                            rows={isEmbedded ? 3 : 2}
                        />
                    </div>

                    <div style={styles.questionContainer}>
                        <label id="lable3" style={styles.label}>
                            {FeedbackConstants.IssueBlockerMessage}
                        </label>
                        <textarea
                            value={issueBlocker}
                            aria-labelledby="lable3"
                            onChange={(e) => handleInputChange(FeedbackConstants.IssueBlocker, e.target.value)}
                            style={styles.textarea}
                            rows={isEmbedded ? 3 : 2}
                        />
                    </div>

                    <div style={styles.questionContainer}>
                        <label id="lable4" style={styles.label}>
                            {FeedbackConstants.PerformanceRatingMessage}
                        </label>
                        <textarea
                            value={performanceRating}
                            aria-labelledby="lable4"
                            onChange={(e) => handleInputChange(FeedbackConstants.PerformanceRating, e.target.value)}
                            style={styles.textarea}
                            rows={isEmbedded ? 3 : 2}
                        />
                    </div>

                    <div style={styles.questionContainer}>
                        <label id="lable5" style={styles.label}>
                            {FeedbackConstants.SuggestionsMessage}
                        </label>
                        <textarea
                            value={suggestions}
                            aria-labelledby="lable5"
                            onChange={(e) => handleInputChange(FeedbackConstants.Suggestions, e.target.value)}
                            style={styles.textarea}
                            rows={isEmbedded ? 3 : 2}
                        />
                    </div>
                    <div style={styles.questionContainer}>
                        <label id="lable6" style={styles.label}>{FeedbackConstants.UploadAttachmentsLabel}</label>
                        <input
                            aria-labelledby="lable6"
                            aria-describedby="feedbackAttachmentsHelp"
                            ref={fileInputRef}
                            style={styles.label}
                            type="file"
                            multiple
                            onChange={handleFileChange}
                        />
                        <span
                            id="feedbackAttachmentsHelp"
                            style={{
                                display: 'block',
                                marginTop: '6px',
                                fontSize: '12px',
                                color: '#6b6b6b'
                            }}
                        >
                            Allowed file formats: Any file type. You may upload one or more attachments.
                        </span>

                        <ul style={{ marginTop: '8px' }}>
                            {attachmentList.map((file, i) => (
                                <li style={{ ...styles.label, ...styles.attachmentListItem }} key={i}>
                                    <span>{file.name}</span>
                                    <button
                                        type="button"
                                        onClick={() => handleRemoveFile(i)}
                                        aria-label={`${FeedbackConstants.DeleteAttachmentButtonLabel} ${file.name}`}
                                        title={`${FeedbackConstants.DeleteAttachmentButtonLabel} ${file.name}`}
                                        style={styles.deleteAttachmentButton}
                                    >
                                        &times;
                                    </button>
                                </li>
                            ))}
                        </ul>
                    </div>
                    {(!isEmbedded || applicationModule == 1) && (
                        <div style={{ ...styles.questionContainer, display: 'flex', alignItems: 'center', gap: '10px' }}>
                            <input
                                type="checkbox"
                                id="includeScreenshot"
                                checked={includeScreenshot}
                                onChange={e => setIncludeScreenshot(e.target.checked)}
                                style={{ width: '18px', height: '18px' }}
                            />
                            <label htmlFor="includeScreenshot" style={{ ...styles.label, marginBottom: 0, cursor: 'pointer' }}>
                                {FeedbackConstants.IncludeScreenshotMessage}
                            </label>
                        </div>
                    )}
                    <div style={styles.buttonContainer}>
                        <button onClick={handleSubmit} style={styles.submitButton} aria-describedby={errorMessage ? 'formError' : undefined}>
                            {FeedbackConstants.SubmitFeedbackButtonLabel}
                        </button>
                        {isEmbedded ? (
                            <button onClick={handleReset} style={styles.resetButton}>
                                {FeedbackConstants.ResetFormButtonLabel}
                            </button>
                        ) : (
                            onClose && (
                                <button onClick={onClose} style={styles.cancelButton}>
                                    {FeedbackConstants.CancelButtonLabel}
                                </button>
                            )
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

// Styles
const styles: { [key: string]: CSSProperties } = {
    // Popup-specific styles
    popupOverlay: {
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        zIndex: 1000,
        padding: '15px',
    },
    popupContainer: {
        backgroundColor: 'white',
        borderRadius: '10px',
        boxShadow: '0 4px 20px rgba(0,0,0,0.3)',
        maxWidth: '600px',
        width: '100%',
        maxHeight: '90vh',
        overflow: 'auto',
        position: 'relative',
    },
    header: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: '20px 30px 10px 30px',
        borderBottom: '1px solid #e0e0e0',
    },
    closeXButton: {
        background: 'none',
        border: 'none',
        fontSize: '24px',
        cursor: 'pointer',
        color: '#666',
        padding: '0',
        width: '30px',
        height: '30px',
        borderRadius: '50%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        transition: 'background-color 0.3s',
    },
    popupFormContainer: {
        padding: '20px 30px 30px 30px',
    },

    // Embedded-specific styles
    embeddedContainer: {
        maxWidth: '800px',
        margin: '0 auto',
        padding: '20px',
    },
    embeddedTitle: {
        color: '#2c3e50',
        textAlign: 'center',
        marginBottom: '30px',
        fontSize: '28px',
    },
    embeddedFormContainer: {
        backgroundColor: 'white',
        padding: '30px',
        borderRadius: '10px',
        boxShadow: '0 4px 15px rgba(0,0,0,0.1)',
        border: '1px solid #e0e0e0',
    },

    // Common styles
    title: {
        color: '#2c3e50',
        margin: 0,
        fontSize: '18px',
    },
    ratingContainer: {
        marginBottom: '25px',
        textAlign: 'center',
    },
    label: {
        display: 'block',
        color: 'black',
        marginBottom: '10px',
        fontSize: '12px',
    },
    starsContainer: {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        gap: '5px',
    },
    star: {
        cursor: 'pointer',
        transition: 'color 0.2s',
        userSelect: 'none',
    },
    ratingText: {
        marginLeft: '15px',
        color: '#666',
        fontSize: '14px',
    },
    questionContainer: {
        marginBottom: '25px',
    },
    textarea: {
        width: '100%',
        padding: '12px',
        borderRadius: '6px',
        border: '1px solid #ddd',
        fontSize: '14px',
        fontFamily: 'inherit',
        resize: 'vertical',
        minHeight: '75px',
        boxSizing: 'border-box',
    },
    error: {
        backgroundColor: '#fee',
        border: '1px solid #c0392b',
        color: '#c0392b',
        padding: '6px',
        borderRadius: '6px',
        marginBottom: '10px',
        textAlign: 'center',
    },
    required: {
        color: '#c0392b',
        fontWeight: 'bold',
    },
    helpText: {
        color: '#c0392b',
        fontSize: '12px',
        marginTop: '5px',
        fontStyle: 'italic',
    },
    visuallyHidden: {
        position: 'absolute',
        width: '1px',
        height: '1px',
        padding: 0,
        margin: '-1px',
        overflow: 'hidden',
        clip: 'rect(0 0 0 0)',
        whiteSpace: 'nowrap',
        border: 0
    } as CSSProperties,
    visuallyHiddenInput: {
        position: 'absolute',
        opacity: 0,
        width: '1px',
        height: '1px',
        margin: 0,
        padding: 0,
        overflow: 'hidden',
        border: 0
    } as CSSProperties,
    buttonContainer: {
        display: 'flex',
        gap: '15px',
        justifyContent: 'center',
        marginTop: '30px',
    },
    submitButton: {
        backgroundColor: '#156BE4',
        color: 'white',
        border: 'none',
        padding: '10px 15px',
        borderRadius: '6px',
        cursor: 'pointer',
        fontSize: '14px',
        fontWeight: 'bold',
        transition: 'background-color 0.3s',
    },
    cancelButton: {
        backgroundColor: '#657576',
        color: 'white',
        border: 'none',
        padding: '10px 15px',
        borderRadius: '6px',
        cursor: 'pointer',
        fontSize: '14px',
        fontWeight: 'bold',
        transition: 'background-color 0.3s',
    },
    resetButton: {
        backgroundColor: '#657576',
        color: 'white',
        border: 'none',
        padding: '12px 30px',
        borderRadius: '6px',
        cursor: 'pointer',
        fontSize: '16px',
        fontWeight: 'bold',
        transition: 'background-color 0.3s',
    },
    deleteAttachmentButton: {
        backgroundColor: 'transparent',
        color: '#c0392b',
        border: 'none',
        padding: '0 4px',
        marginLeft: '6px',
        cursor: 'pointer',
        fontSize: '16px',
        fontWeight: 'bold',
        lineHeight: 1,
        verticalAlign: 'middle',
    },
    attachmentListItem: {
        marginBottom: '4px',
    },
    closeButton: {
        backgroundColor: '#3498db',
        color: 'white',
        border: 'none',
        padding: '12px 30px',
        borderRadius: '6px',
        cursor: 'pointer',
        fontSize: '16px',
        fontWeight: 'bold',
        transition: 'background-color 0.3s',
    },
    success: {
        backgroundColor: '#d4edda',
        border: '1px solid #c3e6cb',
        borderRadius: '10px',
        padding: '40px',
        textAlign: 'center',
        margin: '20px',
    },
    successTitle: {
        color: '#155724',
        marginBottom: '15px',
        fontSize: '24px',
        margin: '0 0 15px 0',
    },
    successMessage: {
        color: '#155724',
        marginBottom: '25px',
        fontSize: '16px',
        lineHeight: '1.5',
        margin: '0 0 25px 0',
    },
};

export default UserFeedback;

// Generated by Copilot
// 2026-01-29 00:00:00 | 46 lines added/updated | Added accessible radio-based stars, aria-live rating announcement and screen-reader-only file-upload helper (aria-describedby); preserved existing validations and behavior | Time Taken 6 min

